import {AxiosResponse} from "axios";
import React from "react";
import {NavLink} from "react-router-dom";
import Button from "../../../../common/components/shared/button/button";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {Api} from "../../../../helpers/api";
import {routingAddresses} from "../../../../helpers/routing";
import i18n from "../../../../i18n/i18n";
import {LocationEntryModel} from "../../../models/location";
import {LocationService} from "../../../services/locationService";
import "../../topology.scss";
import "../locations.scss";
import RemovePopup from "../../../../common/components/shared/removePopup/removePopup";
import {IPaginationStore} from "../../../../common/stores/paginationStore";
import {storesContext} from "../../../../common/stores/stores";

interface LocationListState {
    locationData: LocationEntryModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    isTableLoader: boolean;
    selectedElement: LocationEntryModel | undefined;
    removeItemResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayRemovePopup: boolean;
    isPopupActionWaiting: boolean;
    errorMessage: string;
}

export default class LocationsList extends React.Component<any, LocationListState>  {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            locationData: undefined,
            response: undefined,
            currentPage: 0,
            isTableLoader: false,
            selectedElement: undefined,
            removeItemResponse: undefined,
            displayRemovePopup: false,
            isPopupActionWaiting: false,
            errorMessage: ""
        };
    }

    get locationData() {
        return this.state.locationData;
    }

    public async componentDidMount() {
        await this.getAllLocations();
    }

    public render(): JSX.Element {
        const properties = ["locationType", "url", "name", "description"];
        const headers = properties.map(key => i18n.t("topology.locations.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="locations">
                {this.renderWrapperContent(headers, properties, "locations", actions, this.renderCell)}
            </div>
        );
    }

    protected renderWrapperContent(headers: string[], properties: string[], testId: string, actions?: TableActionFunction, renderCell?: (row: any, property: string) => JSX.Element) {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("topology.locations.errors.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.locationData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="location-content">
                            {this.locationData && this.locationData!.length && this.locationData!.length > 0 ? (
                                <Table
                                    headers={headers}
                                    rows={this.state.locationData!}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    onPageChange={this.getAllLocations.bind(this)}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    currentPage={this.state.currentPage}
                                    numberOfElements={0}
                                />
                            )}
                            {this.renderRemovePopup()}
                        </div>
                    </>
                );
            }
        }
    }

    private renderHeader() {
        return (
            <div className="topology-header">
                <NavLink to={routingAddresses.topology.locations.add} data-testid="add-location">
                    <Button type="primary" text={i18n.t("topology.locations.actions.addLocation.button")}/>
                </NavLink>
                <ReloadButton
                    reloadFunc={this.reload.bind(this)}
                    testId="location-list-reload-btn"
                />
            </div>
        );
    }

    private renderActions(element: LocationEntryModel) {
        // @ts-ignore
        const elementId = element.id;

        return (
            <>
                {elementId && (
                    <>
                        <NavLink
                            to={`${routingAddresses.topology.locations.modify}/${elementId}`}
                            className="modify-button"
                            data-testid={`modify-location-${elementId}`}
                        >
                            {i18n.t("topology.locations.actions.modifyLocation.button")}
                        </NavLink>
                        <span className="remove" data-testid={`remove-${elementId}`} onClick={() => {
                            this.setState({selectedElement: element});
                            this.onRemoveAction();
                        }}>
                            {i18n.t("remove")}
                        </span>
                    </>
                )}
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        if(property === "locationType") {
            return <>{i18n.t(`topology.locations.locationType.${row[property]}`)}</>;
        }

        return row[property];
    }

    protected renderRemovePopup() {
        const content = <div>
            <p>{i18n.t("topology.locations.removeAction.line1")}</p>
            <p>{i18n.t("topology.locations.removeAction.line2")}</p>
        </div>;

        return (
            <RemovePopup
                displayPopup={this.state.displayRemovePopup}
                content={content}
                header={i18n.t("topology.locations.removeAction.header")}
                secondHeader={i18n.t("topology.locations.removeAction.secondHeader")}
                actionText={i18n.t("topology.locations.removeAction.actionText")}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmRemove.bind(this)}
                response={this.state.removeItemResponse}
                isWaiting={this.state.isPopupActionWaiting}
                httpErrorMessage={i18n.t("topology.locations.errors.removeErrorSuffix")}
            />
        );
    }

    private async getAllLocations() {
        const paginationStore = this.context.paginationStore as IPaginationStore;

        this.setState({isTableLoader: true});

        const response = await LocationService.getAll({
            offset: paginationStore.offset,
            size: paginationStore.size
        });

        this.setState({
            locationData: response?.data?.data || null,
            response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false,
            selectedElement: undefined
        });

        paginationStore.setNumberOfElements(response?.data?.numberOfElements);
        paginationStore.setNumberOfPages(response?.data?.numberOfPages);
    }

    private async reload() {
        this.setState({locationData: undefined});
        await this.getAllLocations();
    }

    private async confirmRemove() {
        this.setState({removeItemResponse: undefined, isPopupActionWaiting: true});

        const response = await LocationService.removeLocation(this.state.selectedElement!.id);

        this.setState({removeItemResponse: response});

        if (Api.isSuccess(response)) {
            this.reload();
            this.setState({selectedElement: undefined, displayRemovePopup: false});
        } else {
            this.setState({errorMessage: i18n.t("topology.locations.errors.removeErrorSuffix")});
        }
        this.setState({isPopupActionWaiting: false});
    }

    private onRemoveAction() {
        this.setState({displayRemovePopup: true, errorMessage: ""});
    }

    private closePopup() {
        this.setState({displayRemovePopup: false, removeItemResponse: undefined});
    }
}
